import { Paper, ScrollArea } from "@mantine/core";
import { type ReactNode, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Group, Panel, type PanelSize, Separator, usePanelRef } from "react-resizable-panels";

import type { AsidePanelConfig, MainPanelConfig, TopbarConfig } from "@/panels/modules/panels/store/panels.store.types";

import { MainPanel } from "@/panels/modules/panels/components/MainPanel";
import { PanelErrorFallback } from "@/panels/modules/panels/components/PanelErrorFallback";
import {
    calculateLeftPanelSize,
    computePanelSizes,
    computePanelStates,
} from "@/panels/modules/panels/components/PanelsView.helpers";
import { usePanelCollapse } from "@/panels/modules/panels/hooks/usePanelCollapse";
import { usePanelResize } from "@/panels/modules/panels/hooks/usePanelResize";
import { usePersistPanelSize } from "@/panels/modules/panels/hooks/usePersistPanelSize";
import { useVerticalPanelPairResize } from "@/panels/modules/panels/hooks/useVerticalPanelPairResize";

import styles from "./PanelsView.module.css";

const LEFT_MAIN_PANEL_GROUP_ID = "leftMainPanelGroup";
const RIGHT_MAIN_PANEL_GROUP_ID = "rightMainPanelGroup";
const LOCK_ASIDE_PANELS = true;
const LOCK_MAIN_PANELS = false;
const HIT_TARGET_MIN_SIZE = { coarse: 8, fine: 4 };

interface PanelsViewProps {
    // Content slots
    leftSidebar?: ReactNode;
    topbar?: ReactNode;
    rightSidebar?: ReactNode;
    leftTopA?: ReactNode;
    leftTopB?: ReactNode;
    leftBottomA?: ReactNode;
    leftBottomB?: ReactNode;
    rightTopA?: ReactNode;
    rightTopB?: ReactNode;
    rightBottomA?: ReactNode;
    rightBottomB?: ReactNode;

    // Main panel configs
    leftSidebarConfig: AsidePanelConfig;
    topbarConfig: TopbarConfig;
    rightSidebarConfig: AsidePanelConfig;
    leftTopConfig: MainPanelConfig;
    leftBottomConfig: MainPanelConfig;
    rightTopConfig: MainPanelConfig;
    rightBottomConfig: MainPanelConfig;

    // Global panel configs
    leftMainPanelDefaultSize: number;
}

/**
 * Adapter to bridge the old hook signature `(size: number) => void` (a percentage) to
 * react-resizable-panels v4's `(panelSize: PanelSize, id, prev) => void` callback.
 */
const wrapResizeHandler = (handler: (size: number) => void) => (panelSize: PanelSize) => {
    handler(panelSize.asPercentage);
};

export const PanelsView = ({
    leftSidebar,
    topbar,
    rightSidebar,
    leftTopA,
    leftTopB,
    leftBottomA,
    leftBottomB,
    rightTopA,
    rightTopB,
    rightBottomA,
    rightBottomB,
    leftSidebarConfig,
    topbarConfig,
    rightSidebarConfig,
    leftTopConfig,
    leftBottomConfig,
    rightTopConfig,
    rightBottomConfig,
    leftMainPanelDefaultSize,
}: PanelsViewProps) => {
    const sizes = useMemo(
        () => computePanelSizes({ topbarConfig, leftSidebarConfig, rightSidebarConfig }),
        [topbarConfig, leftSidebarConfig, rightSidebarConfig],
    );

    const leftAsidePanelRef = usePanelRef();
    const topbarPanelRef = usePanelRef();
    const rightAsidePanelRef = usePanelRef();
    const leftMainPanelGroupRef = usePanelRef();
    const rightMainPanelGroupRef = usePanelRef();
    const leftTopPanelRef = usePanelRef();
    const leftBottomPanelRef = usePanelRef();
    const rightTopPanelRef = usePanelRef();
    const rightBottomPanelRef = usePanelRef();

    // Compute all panel states and collapsed heights
    const {
        isOneLeftMainPanelFullyClosedValue,
        isOneRightMainPanelFullyClosedValue,
        isOneSideOfMainPanelsFullyClosedValue,
        leftTopCollapsedHeightPixels,
        rightTopCollapsedHeightPixels,
        leftBottomCollapsedHeightPixels,
        rightBottomCollapsedHeightPixels,
    } = computePanelStates({
        leftTopConfig,
        leftBottomConfig,
        rightTopConfig,
        rightBottomConfig,
        leftTopA,
        leftTopB,
        leftBottomA,
        leftBottomB,
        rightTopA,
        rightTopB,
        rightBottomA,
        rightBottomB,
    });

    const { onResize: onLeftSidebarResize } = usePanelCollapse({
        panelRef: leftAsidePanelRef,
        isCollapsed: leftSidebarConfig.isCollapsed,
    });

    const { onResize: onTopbarResize } = usePanelCollapse({
        panelRef: topbarPanelRef,
        isCollapsed: topbarConfig.isCollapsed,
    });

    const { onResize: onRightSidebarResize } = usePanelCollapse({
        panelRef: rightAsidePanelRef,
        isCollapsed: rightSidebarConfig.isCollapsed,
    });

    const { size: lastLeftMainPanelGroupSize, handleResize: handleLeftMainPanelGroupResize } = usePersistPanelSize({
        initialSize: leftMainPanelDefaultSize,
    });

    usePanelResize({
        panelRef: leftMainPanelGroupRef,
        size: calculateLeftPanelSize({
            lastSize: lastLeftMainPanelGroupSize,
            leftTopAIsClosed: leftTopConfig.a.isClosed,
            leftTopBIsClosed: leftTopConfig.b.isClosed,
            leftBottomAIsClosed: leftBottomConfig.a.isClosed,
            leftBottomBIsClosed: leftBottomConfig.b.isClosed,
            rightTopAIsClosed: rightTopConfig.a.isClosed,
            rightTopBIsClosed: rightTopConfig.b.isClosed,
            rightBottomAIsClosed: rightBottomConfig.a.isClosed,
            rightBottomBIsClosed: rightBottomConfig.b.isClosed,
            leftTopA,
            leftTopB,
            leftBottomA,
            leftBottomB,
            rightTopA,
            rightTopB,
            rightBottomA,
            rightBottomB,
        }),
        dependencies: [
            leftTopConfig.a.isClosed,
            leftTopConfig.b.isClosed,
            leftBottomConfig.a.isClosed,
            leftBottomConfig.b.isClosed,
            rightTopConfig.a.isClosed,
            rightTopConfig.b.isClosed,
            rightBottomConfig.a.isClosed,
            rightBottomConfig.b.isClosed,
            leftTopA,
            leftTopB,
            leftBottomA,
            leftBottomB,
            rightTopA,
            rightTopB,
            rightBottomA,
            rightBottomB,
        ],
    });

    const { size: lastLeftTopPanelSize, handleResize: handleLeftTopPanelResize } = usePersistPanelSize();
    useVerticalPanelPairResize({
        topConfig: leftTopConfig,
        bottomConfig: leftBottomConfig,
        topASlot: leftTopA,
        topBSlot: leftTopB,
        bottomASlot: leftBottomA,
        bottomBSlot: leftBottomB,
        lastTopPanelSize: lastLeftTopPanelSize,
        topPanelRef: leftTopPanelRef,
        bottomPanelRef: leftBottomPanelRef,
        topPanelId: "leftTop",
        topCollapsedHeightPixels: leftTopCollapsedHeightPixels,
        bottomPanelId: "leftBottom",
        bottomCollapsedHeightPixels: leftBottomCollapsedHeightPixels,
    });

    const { size: lastRightTopPanelSize, handleResize: handleRightTopPanelResize } = usePersistPanelSize();
    useVerticalPanelPairResize({
        topConfig: rightTopConfig,
        bottomConfig: rightBottomConfig,
        topASlot: rightTopA,
        topBSlot: rightTopB,
        bottomASlot: rightBottomA,
        bottomBSlot: rightBottomB,
        lastTopPanelSize: lastRightTopPanelSize,
        topPanelRef: rightTopPanelRef,
        bottomPanelRef: rightBottomPanelRef,
        topPanelId: "rightTop",
        topCollapsedHeightPixels: rightTopCollapsedHeightPixels,
        bottomPanelId: "rightBottom",
        bottomCollapsedHeightPixels: rightBottomCollapsedHeightPixels,
    });

    return (
        <Group orientation="horizontal" resizeTargetMinimumSize={HIT_TARGET_MIN_SIZE}>
            <Panel
                panelRef={leftAsidePanelRef}
                defaultSize={sizes.leftSidebar.defaultWidth}
                collapsible={true}
                collapsedSize={0}
                minSize={leftSidebarConfig.isCollapsed ? undefined : sizes.leftSidebar.minWidth}
                maxSize={leftSidebarConfig.isCollapsed ? undefined : sizes.leftSidebar.maxWidth}
                onResize={onLeftSidebarResize}
            >
                <Paper radius={0} h="100%" className={styles.panelContainer}>
                    <ScrollArea className={styles.scrollArea}>
                        <ErrorBoundary fallbackRender={PanelErrorFallback}>{leftSidebar}</ErrorBoundary>
                    </ScrollArea>
                </Paper>
            </Panel>

            <Separator
                disabled={LOCK_ASIDE_PANELS}
                className={`${styles.panelResizeHandle} ${styles.panelResizeHandleHorizontal}`}
            />

            <Panel className={styles.mainPanelContainer}>
                <Group orientation={"vertical"} resizeTargetMinimumSize={HIT_TARGET_MIN_SIZE}>
                    <Panel
                        panelRef={topbarPanelRef}
                        collapsible={true}
                        collapsedSize={0}
                        defaultSize={sizes.topbar.defaultHeight}
                        minSize={topbarConfig.isCollapsed ? undefined : sizes.topbar.minHeight}
                        maxSize={topbarConfig.isCollapsed ? undefined : sizes.topbar.maxHeight}
                        onResize={onTopbarResize}
                    >
                        <Paper radius={0} h="100%" className={styles.panelContainer}>
                            <ScrollArea className={styles.scrollArea}>
                                <ErrorBoundary fallbackRender={PanelErrorFallback}>{topbar}</ErrorBoundary>
                            </ScrollArea>
                        </Paper>
                    </Panel>

                    <Separator
                        disabled={LOCK_ASIDE_PANELS}
                        className={`${styles.panelResizeHandle} ${styles.panelResizeHandleVertical}`}
                    />

                    <Panel minSize={sizes.mainPanels.minSize}>
                        <Group orientation="horizontal" resizeTargetMinimumSize={HIT_TARGET_MIN_SIZE}>
                            <Panel
                                panelRef={leftMainPanelGroupRef}
                                id={LEFT_MAIN_PANEL_GROUP_ID}
                                collapsedSize={0}
                                collapsible={isOneSideOfMainPanelsFullyClosedValue}
                                minSize={sizes.mainPanels.minSize}
                                onResize={wrapResizeHandler(handleLeftMainPanelGroupResize)}
                            >
                                <Group orientation="vertical" resizeTargetMinimumSize={HIT_TARGET_MIN_SIZE}>
                                    <Panel
                                        id="leftTop"
                                        panelRef={leftTopPanelRef}
                                        collapsible={true}
                                        collapsedSize={
                                            leftTopCollapsedHeightPixels ? `${leftTopCollapsedHeightPixels}px` : 0
                                        }
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            flexBasis: leftTopCollapsedHeightPixels,
                                        }}
                                        onResize={wrapResizeHandler(handleLeftTopPanelResize)}
                                    >
                                        <MainPanel panelId={"leftTop"} tabASlot={leftTopA} tabBSlot={leftTopB} />
                                    </Panel>

                                    <Separator
                                        disabled={LOCK_MAIN_PANELS || isOneLeftMainPanelFullyClosedValue}
                                        className={`${styles.panelResizeHandle} ${styles.panelResizeHandleVertical} ${isOneLeftMainPanelFullyClosedValue ? styles.panelResizeHandleHidden : ""}`}
                                    />

                                    <Panel
                                        id="leftBottom"
                                        panelRef={leftBottomPanelRef}
                                        collapsible={true}
                                        collapsedSize={
                                            leftBottomCollapsedHeightPixels ? `${leftBottomCollapsedHeightPixels}px` : 0
                                        }
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            flexBasis: leftBottomCollapsedHeightPixels,
                                        }}
                                    >
                                        <MainPanel
                                            panelId={"leftBottom"}
                                            tabASlot={leftBottomA}
                                            tabBSlot={leftBottomB}
                                        />
                                    </Panel>
                                </Group>
                            </Panel>

                            <Separator
                                disabled={LOCK_MAIN_PANELS || isOneSideOfMainPanelsFullyClosedValue}
                                className={`${styles.panelResizeHandle} ${!isOneSideOfMainPanelsFullyClosedValue && styles.panelResizeHandleHorizontal}`}
                            />

                            <Panel
                                panelRef={rightMainPanelGroupRef}
                                id={RIGHT_MAIN_PANEL_GROUP_ID}
                                collapsedSize={0}
                                collapsible={isOneSideOfMainPanelsFullyClosedValue}
                                minSize={sizes.mainPanels.minSize}
                            >
                                <Group orientation="vertical" resizeTargetMinimumSize={HIT_TARGET_MIN_SIZE}>
                                    <Panel
                                        id="rightTop"
                                        panelRef={rightTopPanelRef}
                                        collapsible={true}
                                        collapsedSize={
                                            rightTopCollapsedHeightPixels ? `${rightTopCollapsedHeightPixels}px` : 0
                                        }
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            flexBasis: rightTopCollapsedHeightPixels,
                                        }}
                                        onResize={wrapResizeHandler(handleRightTopPanelResize)}
                                    >
                                        <MainPanel panelId={"rightTop"} tabASlot={rightTopA} tabBSlot={rightTopB} />
                                    </Panel>

                                    <Separator
                                        disabled={LOCK_MAIN_PANELS || isOneRightMainPanelFullyClosedValue}
                                        className={`${styles.panelResizeHandle} ${styles.panelResizeHandleVertical} ${isOneRightMainPanelFullyClosedValue ? styles.panelResizeHandleHidden : ""}`}
                                    />

                                    <Panel
                                        id="rightBottom"
                                        panelRef={rightBottomPanelRef}
                                        collapsible={true}
                                        collapsedSize={
                                            rightBottomCollapsedHeightPixels
                                                ? `${rightBottomCollapsedHeightPixels}px`
                                                : 0
                                        }
                                        minSize={sizes.mainPanels.minSize}
                                        style={{
                                            flexBasis: rightBottomCollapsedHeightPixels,
                                        }}
                                    >
                                        <MainPanel
                                            panelId={"rightBottom"}
                                            tabASlot={rightBottomA}
                                            tabBSlot={rightBottomB}
                                        />
                                    </Panel>
                                </Group>
                            </Panel>
                        </Group>
                    </Panel>
                </Group>
            </Panel>

            <Separator
                disabled={LOCK_ASIDE_PANELS}
                className={`${styles.panelResizeHandle} ${styles.panelResizeHandleHorizontal}`}
            />

            <Panel
                panelRef={rightAsidePanelRef}
                defaultSize={sizes.rightSidebar.defaultWidth}
                collapsible={true}
                collapsedSize={0}
                minSize={rightSidebarConfig.isCollapsed ? undefined : sizes.rightSidebar.minWidth}
                maxSize={rightSidebarConfig.isCollapsed ? undefined : sizes.rightSidebar.maxWidth}
                onResize={onRightSidebarResize}
            >
                <Paper radius={0} h={"100%"} className={styles.panelContainer}>
                    <ScrollArea className={styles.scrollArea}>
                        <ErrorBoundary fallbackRender={PanelErrorFallback}>{rightSidebar}</ErrorBoundary>
                    </ScrollArea>
                </Paper>
            </Panel>
        </Group>
    );
};
